import type { NetworkTableAreaRow, NetworkTableDmaRow } from '@/api/networkTableApi';

/**
 * What the map should do to show a table row. Derived from the bounds the API already returns
 * (Area/DMA geometry, or the extent of the group's POIs for the Unassigned buckets) — no geometry
 * lookup needed.
 */
export type LocateTarget =
    | { type: 'bounds'; bounds: [[number, number], [number, number]] }
    /** A single point, or a group whose extent collapsed to one — fitBounds cannot frame these. */
    | { type: 'point'; center: [number, number] };

interface Bounds {
    minLng: number | null;
    minLat: number | null;
    maxLng: number | null;
    maxLat: number | null;
}

/**
 * Null when the row cannot be located: an Unassigned group whose POIs have no coordinates has no
 * extent, and there is nothing to fly to. Callers disable the control in that case.
 */
export function locateTargetFromBounds(row: Bounds): LocateTarget | null {
    const { minLng, minLat, maxLng, maxLat } = row;
    if (minLng === null || minLat === null || maxLng === null || maxLat === null) return null;

    // A degenerate extent (one POI, or several at the same coordinate) would make fitBounds
    // zoom to its maximum on a zero-area box.
    if (minLng === maxLng && minLat === maxLat) return { type: 'point', center: [minLng, minLat] };

    return {
        type: 'bounds',
        bounds: [
            [minLng, minLat],
            [maxLng, maxLat],
        ],
    };
}

export function areaLocateTarget(area: NetworkTableAreaRow): LocateTarget | null {
    return locateTargetFromBounds(area);
}

export function dmaLocateTarget(dma: NetworkTableDmaRow): LocateTarget | null {
    // The bounds alone decide: a group only exists when it has POIs, and platform.poi.point is NOT
    // NULL, so a "no locatable POIs" case cannot arise. The bounds check below still guards it.
    return locateTargetFromBounds(dma);
}
